import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { CATEGORIES } from "@/lib/mockData";
import GrantCard from "@/components/common/GrantCard";
import MatchRing from "@/components/common/MatchRing";
import { apiGet } from "@/lib/api";
import { toast } from "sonner";
import { Search, SlidersHorizontal, X, Sparkles, Loader2, Wand2, Building2, CalendarDays, ExternalLink, ArrowUpRight, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Grants() {
    const { scoredGrants, startApplication, applications } = useApp();
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [diff, setDiff] = useState("all");
    const [sort, setSort] = useState("match");

    // --- Federal (Grants.gov) AI re-rank -------------------------------------
    const [liveMatches, setLiveMatches] = useState([]);
    const [liveLoading, setLiveLoading] = useState(true);
    const [liveError, setLiveError] = useState(null);
    const [starting, setStarting] = useState(null);

    const loadLive = async () => {
        setLiveLoading(true);
        setLiveError(null);
        try {
            const r = await apiGet("/grants/match");
            setLiveMatches(Array.isArray(r?.matches) ? r.matches : []);
        } catch (e) {
            setLiveError(e?.response?.data?.detail || "Couldn't reach Grants.gov right now.");
        } finally {
            setLiveLoading(false);
        }
    };
    useEffect(() => { loadLive(); /* eslint-disable-next-line */ }, []);

    const startFromLive = async (hit) => {
        try {
            setStarting(hit.id);
            await startApplication({
                type: "grant",
                target_id: hit.id,
                target_title: hit.title,
                target_amount: 0,
                target_meta: { source: "grants.gov", agency: hit.agency, number: hit.opportunity_number, url: hit.url, match_why: hit.match_why },
            });
            toast.success("Application opened", { description: "Open it from Applications to let AI prepare the packet." });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't open the application");
        } finally { setStarting(null); }
    };
    // -------------------------------------------------------------------------

    const filtered = useMemo(() => {
        let list = scoredGrants.filter((g) => {
            if (cat !== "all" && g.category !== cat) return false;
            if (diff !== "all" && g.difficulty !== diff) return false;
            if (q && !(`${g.title} ${g.description} ${g.org} ${g.tags.join(" ")}`.toLowerCase().includes(q.toLowerCase()))) return false;
            return true;
        });
        if (sort === "amount") list = [...list].sort((a, b) => b.amount - a.amount);
        else if (sort === "deadline") list = [...list].sort((a, b) => (a.deadline || "").localeCompare(b.deadline || ""));
        return list;
    }, [scoredGrants, q, cat, diff, sort]);

    const clearFilters = () => { setQ(""); setCat("all"); setDiff("all"); setSort("match"); };
    const hasFilters = q || cat !== "all" || diff !== "all" || sort !== "match";

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Matches</p>
                <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">{filtered.length} grants surfaced</h1>
                <p className="mt-3 text-muted-foreground max-w-2xl">Sorted by eligibility fit. Higher match score means your profile signals strongly align with published criteria.</p>
            </div>

            {/* Federal AI re-rank — Grants.gov live + Claude eligibility scoring */}
            <section className="mt-10" data-testid="grants-ai-section">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 border border-accent/30 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-accent-foreground">
                            <Sparkles className="h-3 w-3" /> AI · Federal
                        </div>
                        <h2 className="mt-3 font-serif text-2xl sm:text-3xl leading-tight">Live federal grants, ranked for your profile</h2>
                        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                            Pulled straight from Grants.gov, then re-ranked by Claude against your vault. One tap opens a draft — Owed AI prepares the packet from there.
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadLive} disabled={liveLoading} className="rounded-full" data-testid="grants-ai-refresh-btn">
                        {liveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</>}
                    </Button>
                </div>

                {liveLoading ? (
                    <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {[0, 1, 2].map(i => (
                            <Card key={i} className="p-6 border-border animate-pulse">
                                <div className="h-3 w-20 bg-muted rounded" />
                                <div className="mt-3 h-5 w-full bg-muted rounded" />
                                <div className="mt-2 h-5 w-2/3 bg-muted rounded" />
                                <div className="mt-6 h-3 w-full bg-muted rounded" />
                                <div className="mt-2 h-3 w-1/2 bg-muted rounded" />
                            </Card>
                        ))}
                    </div>
                ) : liveError ? (
                    <Card className="mt-6 p-6 border-border border-dashed" data-testid="grants-ai-error">
                        <p className="text-sm text-muted-foreground">{liveError}</p>
                        <Button variant="outline" size="sm" onClick={loadLive} className="mt-3 rounded-full"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Try again</Button>
                    </Card>
                ) : liveMatches.length === 0 ? (
                    <Card className="mt-6 p-6 border-border border-dashed" data-testid="grants-ai-empty">
                        <p className="text-sm text-muted-foreground">No federal matches yet — complete more of your profile to unlock tailored federal opportunities.</p>
                    </Card>
                ) : (
                    <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {liveMatches.slice(0, 6).map((hit) => {
                            const existing = applications.find(a => a.target_id === hit.id);
                            const score = Math.max(0, Math.min(100, hit.match_score || 0));
                            return (
                                <Card key={hit.id} className="p-5 border-border flex flex-col h-full hover:border-foreground/30 transition-colors" data-testid={`grants-ai-card-${hit.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                <Building2 className="h-3 w-3 shrink-0" />
                                                <span className="truncate font-mono uppercase tracking-wider">{hit.agency || "Federal"}</span>
                                            </div>
                                            <h3 className="mt-1.5 font-serif text-lg leading-tight line-clamp-2">{hit.title}</h3>
                                        </div>
                                        <MatchRing score={score} size={44} />
                                    </div>
                                    {hit.match_why && (
                                        <p className="mt-3 text-xs text-foreground/80 leading-relaxed italic line-clamp-3" data-testid={`grants-ai-why-${hit.id}`}>
                                            <Sparkles className="inline h-3 w-3 mr-1 text-accent-foreground" />
                                            {hit.match_why}
                                        </p>
                                    )}
                                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                                        {hit.opportunity_number && <Badge variant="outline" className="bg-secondary/60 border-border text-[10px] font-mono">{hit.opportunity_number}</Badge>}
                                        {hit.close_date && (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                                <CalendarDays className="h-3 w-3" /> {hit.close_date}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-auto pt-4 flex items-center gap-2 border-t border-border mt-4">
                                        <a href={hit.url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                                            Grants.gov <ExternalLink className="h-3 w-3" />
                                        </a>
                                        {existing ? (
                                            <Button asChild size="sm" variant="outline" className="rounded-full ml-auto h-8">
                                                <a href={`/app/detail/grant/${hit.id}`}>Resume <ArrowUpRight className="h-3 w-3 ml-1" /></a>
                                            </Button>
                                        ) : (
                                            <Button size="sm" onClick={() => startFromLive(hit)} disabled={starting === hit.id} className="rounded-full ml-auto h-8 bg-foreground text-background hover:bg-foreground/90" data-testid={`grants-ai-start-${hit.id}`}>
                                                {starting === hit.id ? <Loader2 className="h-3 w-3 animate-spin" /> : (<><Wand2 className="h-3 w-3 mr-1" /> Start + AI prep</>)}
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </section>

            <div className="mt-14 border-t border-border pt-10">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Curated catalog</p>
                <h2 className="mt-2 font-serif text-3xl leading-tight">Owed's vetted shortlist</h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">Programs we've hand-verified — including state + local options Grants.gov doesn't index.</p>
            </div>

            {/* Filters */}
            <Card className="mt-8 p-4 border-border flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0 rounded-full border border-border bg-secondary/40 px-4 h-10">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search grants, agencies, tags…" className="border-0 bg-transparent h-9 focus-visible:ring-0 px-0 text-sm placeholder:text-muted-foreground" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={cat} onChange={setCat} options={[{ value: "all", label: "All categories" }, ...CATEGORIES.map((c) => ({ value: c.id, label: c.label }))]} />
                    <Select value={diff} onChange={setDiff} options={[{ value: "all", label: "Any difficulty" }, { value: "Easy", label: "Easy" }, { value: "Medium", label: "Medium" }, { value: "Hard", label: "Hard" }]} />
                    <Select value={sort} onChange={setSort} options={[{ value: "match", label: "Best match" }, { value: "amount", label: "Highest amount" }, { value: "deadline", label: "Soonest deadline" }]} />
                    {hasFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground"><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
                    )}
                </div>
            </Card>

            {/* Category chips */}
            <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
                <Chip active={cat === "all"} onClick={() => setCat("all")}>All</Chip>
                {CATEGORIES.map((c) => (
                    <Chip key={c.id} active={cat === c.id} onClick={() => setCat(c.id)}>{c.label}</Chip>
                ))}
            </div>

            {filtered.length === 0 ? (
                <Card className="mt-8 p-12 text-center border-dashed border-border">
                    <SlidersHorizontal className="h-6 w-6 text-muted-foreground mx-auto" />
                    <h3 className="mt-3 font-serif text-2xl">No matches with these filters</h3>
                    <p className="mt-2 text-muted-foreground text-sm">Try broadening your criteria or clearing filters.</p>
                    <Button onClick={clearFilters} className="mt-5 rounded-full bg-foreground text-background hover:bg-foreground/90">Reset filters</Button>
                </Card>
            ) : (
                <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filtered.map((g) => <GrantCard key={g.id} grant={g} />)}
                </div>
            )}
        </div>
    );
}

function Chip({ active, onClick, children }) {
    return (
        <button onClick={onClick} className={cn("shrink-0 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors", active ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border hover:text-foreground")}>
            {children}
        </button>
    );
}

function Select({ value, onChange, options }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-full border border-border bg-card text-sm px-4 pr-8 appearance-none bg-no-repeat focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 cursor-pointer" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23555'%3E%3Cpath d='M3 4l3 3 3-3' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E\")", backgroundPosition: "right 12px center" }}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}
